import { createAsyncThunk } from '@reduxjs/toolkit';
import FileSaver from 'file-saver';
import { unparse } from 'papaparse';
import api from 'src/app/api';
import { buildEncodedQueryString, convertToCSVFormat, CSVDataType, downloadCsv } from 'src/common';
import { PagedResult } from 'src/store';
import {
  DownloadSecurityPricePayload,
  SecurityListParameters,
  SecurityPriceItem,
  SecurityPriceStatistics,
  ThunkParameters,
  UpdateSecurityPriceRequest,
} from './types';

export enum SecurityPriceActionTypes {
  FetchSecurityPriceStatistics = '@@security/price/FetchSecurityPriceStatistics',
  FetchSecurityPrices = '@@security/price/FetchSecurityPrices',
  DownloadSecurityPrices = '@@security/price/DownloadSecurityPrices',
  FetchDailySecurityPricesChart = '@@security/price/FetchDailySecurityPricesChart',
  FetchSecurityPricesDownloadAll = '@@security/price/FetchSecurityPricesDownloadAll',
}

export enum SecurityPriceApiEndpoints {
  fetchSecurityPriceStatistics = '/securities/GetSecurityPriceStatistics',
  fetchSecurityPrices = '/api/performances/security/GetPrices',
  fetchDailySecurityPricesChart = '/api/performances/security/GetDailyPricesChart',
  RequestHistoricalSecurityPrices = '/securities/GetHistoricalEndOfDaySecurityPrices',
}

export const fetchSecurityPriceStatistics = createAsyncThunk(
  SecurityPriceActionTypes.FetchSecurityPriceStatistics,
  async (wrapper: ThunkParameters) => {
    const response = await api.get<SecurityPriceStatistics>(
      `${SecurityPriceApiEndpoints.fetchSecurityPriceStatistics}?securityId=${wrapper.securityId}&fromDate=${wrapper.dates.dateFrom}&toDate=${wrapper.dates.dateTo}`
    );
    return response.data;
  }
);

export const fetchSecurityPriceChart = createAsyncThunk(
  SecurityPriceActionTypes.FetchDailySecurityPricesChart,
  async (wrapper: ThunkParameters) => {
    const response = await api.get<SecurityPriceItem[]>(
      `${SecurityPriceApiEndpoints.fetchDailySecurityPricesChart}?securityId=${wrapper.securityId}&fromDate=${wrapper.dates.dateFrom}&toDate=${wrapper.dates.dateTo}`
    );
    return response.data;
  }
);

export const fetchSecurityPrices = createAsyncThunk(
  SecurityPriceActionTypes.FetchSecurityPrices,
  async (wrapper: SecurityListParameters) => {
    const queryString = buildEncodedQueryString({ pagedRequest: wrapper.pagination, securityId: wrapper.securityId });
    const response = await api.get<PagedResult<SecurityPriceItem>>(
      `${SecurityPriceApiEndpoints.fetchSecurityPrices}${queryString}`
    );
    return {
      results: response.data,
      pagination: wrapper.pagination,
    };
  }
);

export const fetchSecurityPricesExport = createAsyncThunk(
  SecurityPriceActionTypes.FetchSecurityPricesDownloadAll,
  async (wrapper: SecurityListParameters) => {
    const queryString = buildEncodedQueryString({ pagedRequest: wrapper.pagination, securityId: wrapper.securityId });

    const response = await api.get<PagedResult<SecurityPriceItem>>(
      `${SecurityPriceApiEndpoints.fetchSecurityPrices}${queryString}`
    );

    if (response) {
      const csv = unparse(response.data.results);
      const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      FileSaver.saveAs(csvData, 'WO2_security_prices.csv');
    }
  }
);

const getSecurityPrices = async (request: SecurityListParameters) => {
  const queryString = buildEncodedQueryString({
    pagedRequest: request.pagination,
    securityId: request.securityId,
  });
  return await api.get<PagedResult<SecurityPriceItem>>(
    `${SecurityPriceApiEndpoints.fetchSecurityPrices}${queryString}`
  );
};

export const requestHistoricalEndOfDaySecurityPrices = createAsyncThunk(
  SecurityPriceApiEndpoints.RequestHistoricalSecurityPrices,
  async (payload: UpdateSecurityPriceRequest, thunkApi) => {
    const promise = api.post(SecurityPriceApiEndpoints.RequestHistoricalSecurityPrices, payload);
    return await promise
      .then(() => {
        const securitylistParameters: SecurityListParameters = {
          pagination: {
            pageNumber: 1,
            pageSize: 25,
            queryFields: [
              {
                fieldName: 'date',
                descendingSortDirection: true,
                operator: '',
                hasSearchTerm: false,
                isSortTerm: true,
                searchTerm: '',
              },
            ],
          },
          securityId: payload.securityId,
        };
        return thunkApi.dispatch(fetchSecurityPrices(securitylistParameters));
      })
      .catch(() => {
        return { message: 'failed to load security prices', variant: 'error' };
      });
  }
);

export const downloadSecurityPrices = createAsyncThunk(
  SecurityPriceActionTypes.DownloadSecurityPrices,
  async (wrapper: DownloadSecurityPricePayload) => {
    const response = await getSecurityPrices({
      ...wrapper,
      pagination: { ...wrapper.pagination, pageNumber: 1, pageSize: 9999999 },
    });
    const data: CSVDataType[] = convertToCSVFormat(response.data.results);

    downloadCsv(data, `SecurityPrices_`);
    return { message: 'CSV downloaded' };
  }
);
